/**
 * 📋 Unified Work Item Types — Minha Mesa + Equipe
 *
 * Aggregates tasks from all platform sources:
 * - Actions (plano de ação)
 * - Workflow tasks (v9)
 * - Problem investigations (MASP)
 * - Monitoring tasks
 */

// ─── Source Identification ──────────────────────────────────

export type WorkItemSource = 'action' | 'workflow' | 'problem' | 'monitoring';

export type WorkItemStatus = 'pending' | 'in_progress' | 'waiting' | 'completed';

// ─── Unified Work Item ──────────────────────────────────────

export interface UnifiedWorkItem {
  id: string;
  source: WorkItemSource;
  source_id: string;
  title: string;
  description?: string | null;
  status: WorkItemStatus;
  assigned_to: string;
  assigned_to_name?: string | null;
  priority?: string | null;
  due_date?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  is_overdue: boolean;
  organization_id: string;
  project_id?: string | null;

  // Source-specific context
  source_context?: WorkItemContext;
}

export interface WorkItemContext {
  // Action fields
  action_type?: string;
  action_status?: string;

  // Workflow fields
  instance_title?: string;
  stage_name?: string;
  sla_deadline?: string | null;

  // Problem fields
  issue_severity?: string;
  issue_type?: string;
  issue_mode?: string;
  issue_status?: string;

  // Monitoring fields
  monitoring_mode?: string;
  last_check_at?: string | null;
}

// ─── Dashboard KPIs ─────────────────────────────────────────

export interface WorkDashboardKPIs {
  tasks_in_progress: number;
  tasks_pending: number;
  tasks_overdue: number;
  tasks_completed_today: number;
}

// ─── Team Types ─────────────────────────────────────────────

export type LoadStatus = 'normal' | 'high' | 'overloaded';

export interface TeamMemberWorkload {
  user_id: string;
  user_name: string;
  role_label?: string;
  tasks_in_progress: number;
  tasks_pending: number;
  tasks_overdue: number;
  tasks_completed_today: number;
  total_active: number;
  avg_sla_minutes: number | null;
  sla_violated_pct: number;
  bottleneck_score: number;
  load_status: LoadStatus;
}

export interface TeamDashboardKPIs {
  tasks_per_employee: number;
  overdue_tasks: number;
  average_completion_time: number | null; // days
  current_workload: number;
  total_members: number;
  total_sla_violated: number;
  avg_sla_compliance_pct: number;
}

// ─── Labels ─────────────────────────────────────────────────

export const WORK_SOURCE_LABELS: Record<WorkItemSource, string> = {
  action: 'Ação',
  workflow: 'Workflow',
  problem: 'Problema',
  monitoring: 'Monitoramento',
};

export const WORK_STATUS_LABELS: Record<WorkItemStatus, string> = {
  pending: 'Pendente',
  in_progress: 'Em Andamento',
  waiting: 'Aguardando',
  completed: 'Concluído',
};

export const WORK_SOURCE_COLORS: Record<WorkItemSource, string> = {
  action: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  workflow: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  problem: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  monitoring: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
};

export const WORK_STATUS_COLORS: Record<WorkItemStatus, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  waiting: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
};
